<template>
    <div class="search-overlay" :class="{ active: searchOpen }" @click="close">
        <transition name="slide-fade">
            <div class="search-wrapper" v-if="searchOpen" :class="activeScreen">
                <form id="search-form" :class="formClasses" @submit.prevent="performSearch()">
                    <div class="search-field">
                        <svg
                            width="16px"
                            height="16px"
                            viewBox="0 0 18 20"
                            version="1.1"
                            xmlns="http://www.w3.org/2000/svg"
                            xmlns:xlink="http://www.w3.org/1999/xlink"
                        >
                            <g id="Search-/-Explore" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                                <g
                                    id="Main---New-Nav"
                                    transform="translate(-108.000000, -746.000000)"
                                    stroke-width="1.2"
                                >
                                    <g id="Group-3" transform="translate(109.000000, 747.000000)">
                                        <circle id="Oval" cx="6.8" cy="6.8" r="6.8"></circle>
                                        <line
                                            x1="11.2"
                                            y1="12"
                                            x2="16"
                                            y2="17.6"
                                            id="Line-8"
                                            stroke-linecap="round"
                                        ></line>
                                    </g>
                                </g>
                            </g>
                        </svg>
                        <input
                            type="text"
                            placeholder="Search"
                            v-model="searchInput"
                            @input="handleInputChange()"
                            v-on:keyup.enter="$event.target.blur()"
                        />
                    </div>
                    <div class="active-brand" v-show="brandFilter.product_brand_id || brandFilter.affiliate_id">
                        {{ brandFilter.product_brand_title || brandFilter.affiliate_title }} ({{ searchResult.total }}
                        products)
                        <a class="cross" v-on:click="searchByBrand({})"></a>
                    </div>
                </form>
                <full-view-select
                    v-if="activeScreen == 'search-result'"
                    :options="sortOptions"
                    :optionSelectedIndex="sortOptionSelected.index"
                    defaultValueText="Sort By"
                    optionTextField="title"
                    @option-changed="setSortOption"
                    @closed="() => setSortOption(null, null)"
                />
                <div class="wtw-container" v-if="showBackButton">
                    <span class="stay-black back-to-search-index" @click="goBack">Back</span>
                </div>
                <div class="search-content" id="searchContent" ref="searchContent" infinite-wrapper>
                    <transition name="fade">
                        <div v-if="dataLoading && activeScreen == 'search-result'">
                            <div class="loading-overlay">
                                <loader></loader>
                            </div>
                        </div>
                    </transition>
                    <search-landing
                        :handleOnBrandClick="searchByBrand"
                        :handleOnCollectionClick="goToCollectionView"
                        :handleCtaClick="goToBrandsList"
                        :brands="productBrands.filter((brand) => brand.product_brand_is_featured == 1)"
                        :collections="featuredCollections"
                        v-if="activeScreen == 'search-landing'"
                    />
                    <brands-list
                        :handleOnClick="searchByBrand"
                        :brands="productBrands"
                        v-if="activeScreen == 'brands-list'"
                    />
                    <search-result-products
                        ref="searchResult"
                        :products="searchResult.products"
                        :handleOnClick="goToProduct"
                        :performSearch="performSearch"
                        :setSortOption="setSortOption"
                        v-if="activeScreen == 'search-result'"
                    />
                    <product-single-container
                        :productId="currentProductId"
                        :goBack="goToSearchResult"
                        @change="(newProductId) => (currentProductId = newProductId)"
                        v-if="activeScreen == 'product-view'"
                    />
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import Mixins from "../../mixins/mixins";
import SearchLanding from "./search-landing.vue";
import BrandsList from "./brands-list.vue";
import SearchResultProducts from "./search-result-products.vue";
import ProductSingleContainer from "./product-single-container.vue";
import FullViewSelect from "../common/full-view-select.vue";
import { SCREEN_NAMES_ANALYITICS } from "../../config";
import { trackEvent, trackEventByFirebase } from "../../store/utils/tracking";

import Loader from "../common/loader.vue";

export default {
    mixins: [Mixins],
    data: function() {
        return {
            formActive: false,
            formHidden: false,
            searchInput: "",
            brandFilter: {},
            sortOptionSelected: {
                index: null,
                value: null,
            },
            typingTimeout: null,
            dataLoading: false,
            page: 1,
            currentProductId: null,
            activeScreen: "search-landing",
            savedScrollContentTop: 0,
            lastActiveScreen: "",
        };
    },
    components: {
        SearchLanding,
        SearchResultProducts,
        BrandsList,
        ProductSingleContainer,
        Loader,
        FullViewSelect,
    },
    computed: {
        formClasses() {
            return {
                active: this.formActive,
                hidden: this.formHidden,
                "reduced-padding": this.activeScreen === "brands-list",
                landing: this.activeScreen === "search-landing",
            };
        },
        showBackButton() {
            return this.activeScreen !== "search-landing" && this.activeScreen !== "product-view";
        },
        formClasses() {
            return {
                active: this.formActive,
                hidden: this.formHidden,
                "reduced-padding": this.activeScreen === "brands-list",
                landing: this.activeScreen === "search-landing",
            };
        },
        ...mapGetters({
            searchOpen: "search/searchOpen",
            searchResult: "search/searchResult",
            productBrands: "search/productBrands",
            featuredCollections: "search/featuredCollections",
            sortOptions: "filters/getSortOptions",
        }),
    },
    methods: {
        close(event) {
            if (event.target.className == "search-overlay active") {
                this.closeMenu();
            }
        },
        closeMenu() {
            this.$store.commit({
                type: "search/setSearchOpen",
                searchOpen: false,
            });
        },
        handleInputChange() {
            if (this.searchInput?.length >= 3 || this.brandFilter.product_brand_id || this.brandFilter.affiliate_id) {
                this.formActive = true;
                clearTimeout(this.typingTimeout);
                this.typingTimeout = setTimeout(() => {
                    this.page = 1;
                    if (this.$refs.searchResult) {
                        this.$refs.searchResult.$refs.infiniteLoading.stateChanger.reset();
                    }
                    if (this.$refs.searchContent) {
                        this.$refs.searchContent.scrollTo(0, 0);
                    }
                    this.performSearch();
                }, 500);
            } else {
                clearTimeout(this.typingTimeout);
                this.formActive = false;
                this.activeScreen = "search-landing";
            }
        },
        searchByBrand(brand) {
            this.brandFilter = brand;
            if (this.brandFilter.product_brand_id || this.brandFilter.affiliate_id || this.searchInput?.length > 3) {
                this.formActive = true;
                this.page = 1;
                this.performSearch();
            } else {
                this.formActive = false;
                this.page = 1;
                this.activeScreen = "search-landing";
            }
        },
        setSortOption(index, value) {
            this.sortOptionSelected = {
                index,
                value: value && `${value.type}_${value.order}`,
            };
            this.performSearch();
        },
        performSearch($state) {
            if (this.activeScreen != "search-result") {
                if ($state) {
                    /**
                     * Since scroll is on a parent item, we need to perform infinite scroll
                     * only when on the search result page
                     */
                    $state.loaded();
                    return;
                }

                this.setLastActiveScreen();
            }
            this.dataLoading = !$state;

            this.activeScreen = "search-result";

            let filters = {};
            if (this.brandFilter.product_brand_id) {
                filters.brand = this.brandFilter.product_brand_id;
            }

            if (this.brandFilter.affiliate_id) {
                filters.affiliate = this.brandFilter.affiliate_id;
            }

            // If any input/filter changed, reset page
            if (!$state) {
                this.page = 1;
            }

            trackEvent("search", {
                query: this.searchInput && this.searchInput.toLowerCase(),
                brand_id: this.brandFilter.product_brand_id,
                affiliate_id: this.brandFilter.affiliate_id,
            });

            this.$store
                .dispatch({
                    type: "search/performSearch",
                    query: this.searchInput,
                    page: this.page,
                    brand: this.brandFilter?.product_brand_id || null,
                    sort: this.sortOptionSelected.value,
                })
                .then((products) => {
                    trackEventByFirebase("search-completed");

                    if (products.length === 0) {
                        if ($state) {
                            $state.complete();
                        }
                    } else {
                        if ($state) {
                            this.page++;
                            $state.loaded();
                        }
                    }
                })
                .catch((err) => {
                    trackEventByFirebase("search-error", err);

                    if ($state) {
                        $state.error();
                    }
                })
                .finally(() => {
                    this.dataLoading = false;
                });

            return false;
        },
        goToBrandsList() {
            this.activeScreen = "brands-list";
            this.formActive = true;
            this.brandFilter = {};
        },
        goToCollectionView(collection) {
            this.formHidden = true;
            this.$router.push({
                path: "/shop/featured-collections/" + collection.id, // Passing the ID so that the URL is different and the page reloads, making the outfits refresh
                query: {
                    collectionId: collection.id,
                },
            });
        },
        goToProduct(product) {
            this.savedScrollContentTop = this.$refs.searchContent.scrollTop;
            this.currentProductId = product.product_id;
            this.activeScreen = "product-view";
            this.formHidden = true;
        },
        goToSearchResult() {
            this.setLastActiveScreen();
            this.activeScreen = "search-result";
            this.formHidden = false;
        },
        goBack() {
            switch (this.lastActiveScreen) {
                case "brands-list":
                    this.goToBrandsList();
                    break;
                case "search-landing":
                default:
                    this.goToSearchIndex();
            }
            this.lastActiveScreen = "";
        },
        setLastActiveScreen() {
            if (!this.lastActiveScreen) this.lastActiveScreen = this.activeScreen;
        },
        goToSearchIndex() {
            this.savedScrollContentTop = 0;
            this.activeScreen = "search-landing";
            this.formActive = false;
            this.searchInput = null;
            this.sortOptionSelected = {
                index: null,
                value: null,
            };
            this.brandFilter = {};
        },
    },
    mounted() {
        this.$store.dispatch({
            type: "search/loadBrands",
        });
        this.$store.dispatch({
            type: "search/loadFeaturedCollections",
        });
    },
    watch: {
        activeScreen(value) {
            if (value === "search-result" && this.$refs.searchContent) {
                this.$nextTick(() => {
                    this.$refs.searchContent.scrollTo(0, this.savedScrollContentTop);
                });
            }
            this.setScreenNameForAnalytics(SCREEN_NAMES_ANALYITICS.SEARCH[value]);
        },
        searchOpen(value) {
            if (value) {
                this.setScreenNameForAnalytics(SCREEN_NAMES_ANALYITICS.SEARCH[this.activeScreen]);
            } else {
                this.setScreenNameToLastRoute();
            }
        },
    },
};
</script>

<style>
.loading-overlay {
    position: fixed;
}
</style>
